/**
 * DealListItem — Single deal entry in the sidebar deals list.
 * Shows address/title, stage badge, relative time, and kebab menu.
 */

import { useState } from "react";
import DealStageBadge from "@/components/shared/DealStageBadge";
import DealActionMenu, { DealActionMenuTrigger } from "@/components/shared/DealActionMenu";
import type { DealData } from "@/api/client";

interface DealListItemProps {
  deal: DealData;
  isActive: boolean;
  onClick: () => void;
  onUpdate: (updated: DealData) => void;
  onOpenDetail?: () => void;
}

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default function DealListItem({ deal, isActive, onClick, onUpdate, onOpenDetail }: DealListItemProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const displayName = deal.address || deal.title || deal.parcel_id || "Untitled Deal";

  return (
    <div
      className={`deal-item ${isActive ? "active" : ""}`}
      onClick={onClick}
    >
      <div className="deal-item-top">
        <span className="deal-item-name">{displayName}</span>
        <div className="deal-item-right">
          <DealStageBadge stage={deal.status} />
          <DealActionMenuTrigger onClick={() => setMenuOpen(true)} />
        </div>
      </div>
      <span className="deal-item-time">
        {relativeTime(deal.updated_at)}
      </span>
      {menuOpen && (
        <DealActionMenu
          deal={deal}
          onUpdate={(updated) => {
            onUpdate(updated);
            setMenuOpen(false);
          }}
          onClose={() => setMenuOpen(false)}
        />
      )}
    </div>
  );
}
